<template>
  <div class="main-container">
    <!-- Header with title -->
    <div class="header">
      <h1>奇楠沉香标准化种植知识服务平台</h1>
    </div>

    <!-- Navigation menu -->
    <div class="nav-menu">
      <el-menu mode="horizontal" :router="true" class="nav-items">
        <el-menu-item index="/news">新闻动态</el-menu-item>
        <el-menu-item index="/policy">政策法规</el-menu-item>
        <el-menu-item index="/technology">种植技术</el-menu-item>
        <el-menu-item index="/standards">种植标准</el-menu-item>
        <el-menu-item index="/market">课程培训</el-menu-item>
        <el-menu-item index="/price">知识竞赛</el-menu-item>
      </el-menu>
    </div>

    <!-- Carousel Section -->
    <div class="carousel-container">
      <el-carousel height="400px">
        <el-carousel-item v-for="item in carouselImages" :key="item.id">
          <img :src="item.url" :alt="item.title" class="carousel-image" />
          <div class="carousel-caption">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </div>
        </el-carousel-item>
      </el-carousel>
    </div>

    <!-- Main content area with three columns -->
    <div class="main-content">
      <!-- Right column -->
      <div class="content-column tech-column">
        <h2 class="section-title">种植技术</h2>
        <ul class="tech-list">
          <li v-for="item in techList" :key="item.name">
            <i :class="['iconfont', item.icon]"></i>
            <span>{{ item.name }}</span>
          </li>
        </ul>
      </div>
      <!-- Left column -->
      <div class="content-column news-column">
        <h2 class="section-title">新闻动态</h2>
        <ul class="news-list">
          <li v-for="(news, index) in newsList" :key="index" class="news-item">
            <div class="news-content">
              <span class="news-index">{{ index + 1 }}</span>
              <a href="#" class="news-title">{{ news.title }}</a>
              <span class="news-date">{{ news.date }}</span>
            </div>
          </li>
        </ul>
      </div>

      <!-- Middle column -->
      <div class="content-column policy-column">
        <h2 class="section-title">政策法规</h2>
        <ul class="policy-list">
          <li
            v-for="(policy, index) in policyList"
            :key="index"
            class="policy-item"
          >
            <div class="policy-content">
              <span class="policy-index">{{ index + 1 }}</span>
              <a href="#" class="policy-title">{{ policy.title }}</a>
              <span class="policy-date">{{ policy.date }}</span>
              <span class="policy-department">{{ policy.department }}</span>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

// 添加动态加载 iconfont CSS 的函数
onMounted(() => {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "//at.alicdn.com/t/c/font_4850673_ge9c0e0ekjd.css";
  document.head.appendChild(link);
});

// 轮播图数据
const carouselImages = ref([
  {
    id: 1,
    url: "src/images/背景1.0.png",
    title: "电白区春龙奇楠沉香标准化种植示范基地",
    // description: '标准化种植基地'
  },
]);

// 定义技术列表数据
const techList = ref([
  {
    name: "育苗",
    icon: "icon-chenxiangyumiao", // 使用实际的 iconfont 类名
  },
  {
    name: "种植",
    icon: "icon-chenxiangzhongzhi",
  },
  {
    name: "结香",
    icon: "icon-chenxianganzhuang",
  },
  {
    name: "采香",
    icon: "icon-chenxiangcaiji",
  },
  {
    name: "加工",
    icon: "icon-chenxiangSPICES",
  },
]);

const handleImageError = (e: Event) => {
  // 当图片加载失败时，显示一个默认图标
  const target = e.target as HTMLImageElement;
  target.src = "/icons/default.png"; // 确保有一个默认图标
};

const newsList = ref([
  {
    title: "电白舞台剧《沉香缘》获年度最佳，文化传承新篇章！",
    date: "2025-02-28",
  },
  {
    title: "沉香的新生：从野生濒危到人工种植",
    date: "2025-02-26",
  },
  {
    title: "电白沉香文创产品创意大赛终评结果出炉",
    date: "2025-02-22",
  },
  {
    title: "电白：聚焦资源与优势，为高质量发展赋能添翼",
    date: "2025-02-08",
  },
  {
    title: "《今日关注》主播走基层第二站：齐来探秘电白沉香产业致富经",
    date: "2024-12-09",
  },
  {
    title: "沉香题材微电影《寻香记》在观珠沙垌开拍",
    date: "2024-12-05",
  },
  {
    title: "电白沙垌沉香：一缕香韵带动乡村高质量发展",
    date: "2024-11-28",
  },
  {
    title: '"中国沉香之乡"广东电白:香道文化赋能乡村振兴',
    date: "2024-11-26",
  },
  {
    title: "扩绿、护绿、活绿!茂名持续推进绿美生态建设迎新提质",
    date: "2024-11-26",
  },
  {
    title: '电白这棵树"真香"，一年产值近50亿元',
    date: "2024-10-18",
  },
]);

const policyList = ref([
  {
    title: "《广东省岭南中药材保护条例》",
    date: "2025-03-05",
    department: "广东省人民政府",
  },
  {
    title: "《生态产品目录（2024年版）》",
    date: "2024-04-10",
    department: "国务院",
  },
  {
    title: "《沉香栽培和人工结香取香技术规程》",
    date: "2024-06-01",
    department: "国家林业和草原局",
  },
  {
    title: "《生态保护补偿条例》",
    date: "2024-06-01",
    department: "国家林业和草原局",
  },
  {
    title: "《沉香提取物》",
    date: "2023-11-01",
    department: "国家林业和草原局",
  },
  {
    title: "《茂名市电白区沉香种质资源保护管理规定》",
    date: "2023-01-18",
    department: "茂名市电白区人民政府",
  },
  {
    title: "《沉香质量分级》",
    date: "2020-12-29",
    department: "国家林业和草原局",
  },
  {
    title: "《沉香》（LY/T 2904-2017）",
    date: "2018-01-01",
    department: "国家林业和草原局",
  },
]);
</script>

<style scoped>
.main-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.header {
  background-color: #67c23a;
  color: white;
  padding: 20px;
  text-align: center;
}

.header h1 {
  margin: 0;
  font-size: 24px;
}

.nav-menu {
  background-color: #2e5f1e;
}

.nav-items {
  display: flex;
  justify-content: center;
  background-color: transparent;
}

/* 导航菜单样式 */
:deep(.el-menu) {
  border-bottom: none !important;
  background-color: transparent !important;
}

:deep(.el-menu-item) {
  color: #ffffff !important; /* 设置文字颜色为白色 */
  font-size: 18px;
  font-weight: 500;
  transition: all 0.3s ease;
}

/* 悬停状态 */
:deep(.el-menu-item:hover) {
  color: #67c23a !important; /* 悬停时的文字颜色 */
  background-color: rgba(255, 255, 255, 0.1);
}

/* 选中状态 */
:deep(.el-menu-item.is-active) {
  color: #67c23a !important; /* 选中时的文字颜色 */
  background-color: rgba(255, 255, 255, 0.1);
}

/* 为不同的菜单项设置相同的白色 */
:deep(.el-menu-item[index="/news"]),
:deep(.el-menu-item[index="/policy"]),
:deep(.el-menu-item[index="/technology"]),
:deep(.el-menu-item[index="/standards"]),
:deep(.el-menu-item[index="/market"]),
:deep(.el-menu-item[index="/price"]) {
  color: #ffffff !important;
}

.main-content {
  display: flex;
  gap: 20px;
  padding: 20px;
}

/* 调整三栏宽度比例 */
.news-column {
  flex: 0 0 40%; /* 新闻动态占35% */
  max-width: 40%;
}

.policy-column {
  flex: 0 0 40%; /* 政策法规占35% */
  max-width: 40%;
}

.tech-column {
  flex: 0 0 10%; /* 种植技术占25% */
  max-width: 10%;
}

.content-column {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.content-column h2 {
  color: #2e5f1e;
  font-size: 18px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #67c23a;
}

.content-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.content-column li {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.content-column li:hover {
  color: #67c23a;
}

/* 轮播图样式 */
.carousel-container {
  width: 100%;
  margin: 20px 0;
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.carousel-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 20px;
}

.carousel-caption h3 {
  margin: 0;
  font-size: 24px;
}

.carousel-caption p {
  margin: 10px 0 0;
  font-size: 16px;
}

.tech-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tech-list li {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tech-list li:hover {
  color: #67c23a;
  padding-left: 10px;
}

/* 添加图标相关样式 */
.tech-list .iconfont {
  font-size: 24px; /* 调整图标大小 */
  margin-right: 10px;
  color: #2e5f1e; /* 设置图标默认颜色 */
  transition: color 0.3s ease;
}

.tech-list li:hover .iconfont {
  color: #67c23a; /* 悬停时改变图标颜色 */
}

.tech-list span {
  font-size: 16px;
}

.section-title {
  font-size: 20px;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.news-list {
  list-style: none;
  padding: 0;
}

.news-item {
  padding: 15px 0;
  border-bottom: 1px solid #eee;
  transition: all 0.3s ease;
}

.news-item:hover {
  background-color: #f8f8f8;
  padding-left: 15px;
}

.news-content {
  display: flex;
  align-items: center;
  gap: 15px;
}

.news-index {
  min-width: 24px;
  height: 24px;
  background-color: #67c23a;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.news-title {
  flex: 1;
  color: #333;
  text-decoration: none;
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.3s ease;
}

.news-title:hover {
  color: #67c23a;
}

.news-date {
  color: #67c23a;
  font-size: 14px;
  min-width: 100px;
}

/* 前三条新闻特殊样式 */
.news-item:nth-child(-n + 3) .news-index {
  background-color: #e6a23c;
}

.news-item:nth-child(-n + 3) .news-title {
  font-weight: 600;
}

.policy-list {
  list-style: none;
  padding: 0;
}

.policy-item {
  padding: 15px 0;
  border-bottom: 1px solid #eee;
  transition: all 0.3s ease;
}

.policy-item:hover {
  background-color: #f8f8f8;
  padding-left: 15px;
}

.policy-content {
  display: flex;
  align-items: center;
  gap: 15px;
}

.policy-index {
  min-width: 24px;
  height: 24px;
  background-color: #409eff;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.policy-title {
  flex: 1;
  color: #333; /* 黑色字体 */
  text-decoration: none;
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.policy-title:hover {
  color: #409eff;
}

.policy-date {
  color: #67c23a;
  font-size: 14px;
  min-width: 100px;
}

.policy-department {
  color: #909399;
  font-size: 14px;
  min-width: 150px;
  text-align: right;
}

/* 前三条政策特殊样式 */
.policy-item:nth-child(-n + 3) .policy-index {
  background-color: #e6a23c;
}

.policy-item:nth-child(-n + 3) .policy-title {
  font-weight: 600;
}

/* 响应式布局 */
@media screen and (max-width: 1200px) {
  .main-content {
    flex-direction: column;
  }

  .news-column,
  .policy-column,
  .tech-column {
    flex: 1;
    max-width: 100%;
  }
}
</style>
